import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { TbArrowsSort, TbReload } from "react-icons/tb";
import { FaArrowLeftLong } from "react-icons/fa6";
import { IoIosArrowForward } from "react-icons/io";
import { IoStatsChart } from "react-icons/io5";
import { FaDownload } from "react-icons/fa6";

const API_URL = process.env.REACT_APP_API_BASE|| '';

function DeliveryStatistics() {
    const [FromDate, setFromDate] = useState('');
    const [ToDate, setToDate]     = useState('');

    const [stats, setStats]     = useState([]);   // aggregated rows
    const [loading, setLoading] = useState(false);
    const [error, setError]     = useState('');
    const [hasFetched, setHasFetched] = useState(false);

    const [currentPage, setcurrentPage] = useState(1);
    const usersperpage    = 10;
    const indexoflastuser  = currentPage * usersperpage;
    const indexoffirstuser = indexoflastuser - usersperpage;
    const filteruser       = stats.slice(indexoffirstuser, indexoflastuser);
    const totalpages       = Math.ceil(stats.length / usersperpage);

    // ── Summary totals ────────────────────────────────────────────────────────
    const totals = stats.reduce((acc, r) => {
        acc.sending   += r.sending;
        acc.submitted += r.submitted;
        acc.sent      += r.sent;
        acc.delivered += r.delivered;
        acc.read      += r.read;
        acc.failed    += r.failed;
        acc.others    += r.others;
        acc.total     += r.total;
        return acc;
    }, { sending: 0, submitted: 0, sent: 0, delivered: 0, read: 0, failed: 0, others: 0, total: 0 });

    // ── Fetch & aggregate ─────────────────────────────────────────────────────
    // GET /auth/campaign/report  → rows: { id, campaign_name, template_name, template_type, interface, total_count, status, created_at }
    // GET /auth/campaign/:id     → { data: [{ phone_number, variables, status }] }
    //
    // Aggregate: group by (date × template_type), sum statuses
    const fetchAndAggregate = useCallback(async (from, to) => {
        setLoading(true);
        setError('');
        try {
            const res  = await fetch(`${API_URL}/auth/campaign/report`, { credentials: 'include' });
            const json = await res.json();
            if (!json.success) throw new Error('Failed');

            let campaigns = json.data || [];

            // Date range filter
            if (from) campaigns = campaigns.filter(c => new Date(c.created_at) >= new Date(from));
            if (to)   campaigns = campaigns.filter(c => new Date(c.created_at) <= new Date(to + 'T23:59:59'));

            // Fetch per-message statuses for all campaigns
            const allMessages = await Promise.all(
                campaigns.map(async (c) => {
                    try {
                        const r    = await fetch(`${API_URL}/auth/campaign/${c.id}`, { credentials: 'include' });
                        const data = await r.json();
                        return (data.data || []).map(m => ({
                            date:          new Date(c.created_at).toLocaleDateString('en-GB'),
                            template_type: c.template_type || 'marketing',
                            status:        (m.status || 'pending').toLowerCase(),
                        }));
                    } catch { return []; }
                })
            );

            const flat = allMessages.flat();

            // Group by date + template_type
            const grouped = {};
            flat.forEach(m => {
                const key = `${m.date}__${m.template_type}`;
                if (!grouped[key]) {
                    grouped[key] = {
                        date: m.date, type: m.template_type,
                        sending: 0, submitted: 0, sent: 0,
                        delivered: 0, read: 0, failed: 0, others: 0, total: 0,
                    };
                }
                const g = grouped[key];
                g.total++;
                if (m.status === 'pending')   g.sending++;
                if (m.status === 'submitted') g.submitted++;
                if (m.status === 'sent')      { g.sent++; g.submitted++; }
                if (m.status === 'delivered') g.delivered++;
                if (m.status === 'read')      g.read++;
                if (m.status === 'failed')    g.failed++;
                if (!['pending','submitted','sent','delivered','read','failed'].includes(m.status)) g.others++;
            });

            // Sort by date descending
            const rows = Object.values(grouped).sort((a, b) => {
                const [da, db] = [a.date, b.date].map(d => {
                    const parts = d.split('/');
                    return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
                });
                return db - da;
            });

            setStats(rows);
            setHasFetched(true);
            setcurrentPage(1);
        } catch (e) {
            setError('Failed to load statistics.');
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, []);

    // Auto-load on mount with no date filter
    useEffect(() => { fetchAndAggregate('', ''); }, [fetchAndAggregate]);

    const handleReset = () => {
        setFromDate(''); setToDate('');
        fetchAndAggregate('', '');
    };

    // ── CSV Export ────────────────────────────────────────────────────────────
    const handleExport = () => {
        const headers = ['Date', 'Message Type', 'Sending', 'Submitted', 'Sent', 'Delivered', 'Read', 'Total Delivered (D+R)', 'Failed', 'Others', 'Total'];
        const rows = stats.map(r => [
            r.date, r.type, r.sending, r.submitted, r.sent,
            r.delivered, r.read, r.delivered + r.read,
            r.failed, r.others, r.total,
        ]);
        // Totals row
        rows.push(['TOTAL', 'All', totals.sending, totals.submitted, totals.sent,
            totals.delivered, totals.read, totals.delivered + totals.read,
            totals.failed, totals.others, totals.total]);

        const csv = [headers, ...rows].map(row => row.map(v => `"${v ?? ''}"`).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const a    = document.createElement('a');
        a.href     = URL.createObjectURL(blob);
        a.download = 'delivery_statistics.csv';
        a.click();
    };

    const columns = ['ReceivedDate', 'Message Type', 'Sending', 'Submitted', 'Sent', 'Delivered', 'Read', 'Total Delivered (D+R)', 'Failed', 'Others', 'Total'];

    return (
        <div className='dark:bg-[#121519] px-4'>
            {/* Page Header */}
            <div className='border-b border-[#cfcdcd] pb-1 flex items-center gap-2'>
                <Link to='/Report'>
                    <FaArrowLeftLong className='text-[20px] dark:text-white' />
                </Link>
                <div>
                    <h1 className='text-black font-semibold text-[20px] dark:text-white'>Delivery Statistics</h1>
                    <div className='text-[#454546] dark:text-white text-[12px] font-semibold flex items-center cursor-pointer'>
                        <span>Home</span><IoIosArrowForward /><span>Delivery Statistics</span>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            {hasFetched && !loading && (
                <div className='grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 my-4'>
                    {[
                        { label: 'Sending',   value: totals.sending,   color: 'bg-yellow-50 border-yellow-200 text-yellow-700' },
                        { label: 'Submitted', value: totals.submitted, color: 'bg-blue-50 border-blue-200 text-blue-700' },
                        { label: 'Sent',      value: totals.sent,      color: 'bg-indigo-50 border-indigo-200 text-indigo-700' },
                        { label: 'Delivered', value: totals.delivered, color: 'bg-green-50 border-green-200 text-green-700' },
                        { label: 'Read',      value: totals.read,      color: 'bg-purple-50 border-purple-200 text-purple-700' },
                        { label: 'Failed',    value: totals.failed,    color: 'bg-red-50 border-red-200 text-red-600' },
                        { label: 'Others',    value: totals.others,    color: 'bg-gray-50 border-gray-200 text-gray-600' },
                        { label: 'Total',     value: totals.total,     color: 'bg-[#37715d]/10 border-[#37715d]/30 text-[#37715d]' },
                    ].map(c => (
                        <div key={c.label} className={`border rounded-lg px-3 py-2 text-center ${c.color}`}>
                            <div className='text-[18px] font-bold'>{c.value}</div>
                            <div className='text-[10px] font-semibold uppercase tracking-wide'>{c.label}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* Filter Controls */}
            <div className='py-2 w-full dark:bg-[#121519]'>
                <div className='w-full flex flex-wrap gap-2 items-end'>

                    <div className='w-full sm:w-[48%] lg:w-[19%] flex flex-col gap-1'>
                        <label className='text-[#000] text-[12px] font-semibold dark:text-white'>From Date</label>
                        <input
                            value={FromDate}
                            onChange={e => setFromDate(e.target.value)}
                            className='text-[#454546] border-[#d4cfcf] border rounded-md text-[12px] font-semibold px-2 py-2 outline-none dark:bg-[#121519] dark:text-white'
                            type="date"
                        />
                    </div>

                    <div className='w-full sm:w-[48%] lg:w-[19%] flex flex-col gap-1'>
                        <label className='text-[#000] text-[12px] font-semibold dark:text-white'>To Date</label>
                        <input
                            value={ToDate}
                            onChange={e => setToDate(e.target.value)}
                            className='text-[#454546] border-[#d4cfcf] border rounded-md text-[12px] font-semibold px-2 py-2 outline-none dark:bg-[#121519] dark:text-white'
                            type="date"
                        />
                    </div>

                    <div className='flex items-center gap-2 pb-0.5'>
                        <button
                            onClick={() => fetchAndAggregate(FromDate, ToDate)}
                            disabled={loading}
                            className='flex items-center border border-[#37715d] rounded-lg p-2 text-[#37715d] gap-2 hover:bg-[#37715d] hover:text-white disabled:opacity-50 transition-colors'
                        >
                            <IoStatsChart /><span className='text-[12px] font-bold'>Statistics</span>
                        </button>
                        <button
                            onClick={handleReset}
                            disabled={loading}
                            className='flex items-center border border-[#3f7fca] rounded-lg p-2 text-[#3f7fca] gap-2 hover:bg-[#3f7fca] hover:text-white disabled:opacity-50 transition-colors'
                        >
                            <TbReload /><span className='text-[12px] font-bold'>Reset</span>
                        </button>
                        <button
                            onClick={handleExport}
                            disabled={stats.length === 0}
                            className='flex items-center border border-[#ed5050] rounded-lg p-2 text-[#ed5050] gap-2 hover:bg-[#ed5050] hover:text-white disabled:opacity-50 transition-colors'
                        >
                            <FaDownload /><span className='text-[12px] font-bold'>Export</span>
                        </button>
                    </div>

                </div>
            </div>

            {error && (
                <div className='w-full bg-red-50 border border-red-200 text-red-600 text-[12px] px-4 py-2 rounded my-2'>
                    {error}
                </div>
            )}

            {/* Table */}
            <div className='overflow-x-auto'>
                <table className="w-full overflow-hidden border border-[#dee2e6]">
                    <thead>
                        <tr className="text-left">
                            {columns.map(col => (
                                <th key={col} className="p-1 cursor-pointer border border-[#dee2e6] bg-[#f6f9fc] dark:bg-[#1a1f24]">
                                    <div className="flex justify-between items-center gap-1">
                                        <span className='text-[11px] text-[#576071] dark:text-white font-semibold whitespace-nowrap'>{col}</span>
                                        <button type="button" className="text-gray-400 hover:text-black flex-shrink-0"><TbArrowsSort /></button>
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr className="bg-[#f6f9fc] dark:bg-[#121519]">
                                <td colSpan={columns.length} className="py-10 text-center">
                                    <div className="flex items-center justify-center gap-2 text-[12px] text-gray-400">
                                        <div className="w-4 h-4 border-2 border-[#37715d] border-t-transparent rounded-full animate-spin" />
                                        Aggregating delivery data...
                                    </div>
                                </td>
                            </tr>
                        ) : filteruser.length === 0 ? (
                            <tr className="text-center bg-[#f6f9fc] dark:bg-[#121519]">
                                <td colSpan={columns.length} className="py-8 text-[12px] text-gray-400">
                                    {hasFetched ? 'No data found for the selected date range' : 'Select a date range and click Statistics'}
                                </td>
                            </tr>
                        ) : (
                            <>
                                {filteruser.map((row, i) => (
                                    <tr
                                        key={i}
                                        className="text-gray-500 odd:bg-white even:bg-[#edf1f5] dark:odd:bg-[#121519] dark:even:bg-[#1a1f24] hover:bg-[#e8f0fa] dark:hover:bg-[#2a2f35] transition-colors"
                                    >
                                        <td className="p-1 text-[11px] border border-[#dee2e6] dark:text-white font-medium">{row.date}</td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6] dark:text-white capitalize">{row.type}</td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6] text-yellow-600 font-semibold text-center">{row.sending}</td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6] text-blue-600 font-semibold text-center">{row.submitted}</td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6] text-indigo-600 font-semibold text-center">{row.sent}</td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6] text-green-600 font-semibold text-center">{row.delivered}</td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6] text-purple-600 font-semibold text-center">{row.read}</td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6] text-[#37715d] font-bold text-center">{row.delivered + row.read}</td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6] text-red-500 font-semibold text-center">{row.failed}</td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6] dark:text-white text-center">{row.others}</td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6] dark:text-white font-bold text-center">{row.total}</td>
                                    </tr>
                                ))}

                                {/* Totals footer row */}
                                {stats.length > 0 && (
                                    <tr className="bg-[#37715d]/5 dark:bg-[#37715d]/10 font-bold border-t-2 border-[#37715d]/30">
                                        <td className="p-1 text-[11px] border border-[#dee2e6] text-[#37715d]">TOTAL</td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6] text-[#37715d]">All</td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6] text-yellow-600 text-center">{totals.sending}</td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6] text-blue-600 text-center">{totals.submitted}</td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6] text-indigo-600 text-center">{totals.sent}</td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6] text-green-600 text-center">{totals.delivered}</td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6] text-purple-600 text-center">{totals.read}</td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6] text-[#37715d] text-center">{totals.delivered + totals.read}</td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6] text-red-500 text-center">{totals.failed}</td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6] dark:text-white text-center">{totals.others}</td>
                                        <td className="p-1 text-[11px] border border-[#dee2e6] dark:text-white text-center">{totals.total}</td>
                                    </tr>
                                )}
                            </>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className='flex justify-between w-full mt-2'>
                <p className='text-[11px] text-[#3f7fc8] mt-2'>
                    Showing {stats.length === 0 ? 0 : indexoffirstuser + 1} to {Math.min(indexoflastuser, stats.length)} of {stats.length} records
                </p>
                <div className="flex items-center mt-2 space-x-2">
                    <button
                        disabled={currentPage === 1}
                        onClick={() => setcurrentPage(p => p - 1)}
                        className="px-3 py-1 text-[11px] bg-gray-200 rounded disabled:opacity-50 hover:bg-gray-300 transition"
                    >Prev</button>
                    <p className='text-[11px] text-[#3f7fc8]'>{currentPage} page of {totalpages || 1} pages</p>
                    <button
                        disabled={currentPage >= totalpages}
                        onClick={() => setcurrentPage(p => p + 1)}
                        className="px-3 py-1 text-[11px] bg-gray-200 rounded disabled:opacity-50 hover:bg-gray-300 transition"
                    >Next</button>
                </div>
            </div>
        </div>
    );
}

export default DeliveryStatistics;